import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store/rootReducer';
import {
    Table, Button, Badge, Card, Typography, Row, Col,
    message, Modal, Form, InputNumber, Result, Radio, Collapse, Tooltip, Input
} from 'antd';
import {Elements} from '@stripe/react-stripe-js';
import campaignService from '../../../services/campaignService';
import campaignLinkService from '../../../services/campaignLinkService';
import {getCookie} from 'typescript-cookie';
import {useNavigate, useParams} from 'react-router-dom';
import walletService from '../../../services/walletService';
import DepositForm from '../../Wallet/walletDetails/depositForm';
import {loadStripe, Stripe} from '@stripe/stripe-js';
import {formatString, formatValue, useIsMobile, detectPlatform, downloadFile} from '../../../utils/helpers';
import countryService from "../../../services/countryService";
import {Image} from 'antd';
import RichTextViewer from "../../../components/molecules/Forms/RichText/viewer";
import { InfoCircleOutlined } from '@ant-design/icons';
import authService from '../../../services/authService';
import RichText from "../../../components/molecules/Forms/RichText/component";
import DOMPurify from "dompurify";
import GeoRadiusMap from "../../../components/molecules/Forms/GeoRadiusMap/component";
import {
    defaultRadiusKm,
    formatGeographicLocationLabel,
    isRadiusLocation
} from "../../../utils/campaignGeo";
import {trackEvent} from "../../../analytics/posthog";

const {Text, Link} = Typography;
const stripePK = process.env.REACT_APP_STRIPE_PK || '';
const stripePromise: Promise<Stripe | null> = loadStripe(stripePK);

const CampaignDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const [campaignData, setCampaignData] = useState<any | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [hasLinkedCampaign, setHasLinkedCampaign] = useState(false);
    const [promotedCampaignLinkId, setPromotedCampaignLinkId] = useState<string | null>(null);
    const [showTransactionModal, setShowTransactionModal] = useState(false);

    const [showDepositModal, setShowDepositModal] = useState(false);
    const [depositAmount, setDepositAmount] = useState<number | null>(null);
    const [depositStep, setDepositStep] = useState<'input' | 'pay' | 'result'>('input');
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentOk, setPaymentOk] = useState(false);

    const [walletBalance, setWalletBalance] = useState<number>(0);
    const [depositMethod, setDepositMethod] = useState<'wallet' | 'card'>('wallet');

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [mediaFile, setMediaFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const [reviewNeeded, setReviewNeeded] = useState<boolean>(false);
    const [isBlocked, setIsBlocked] = useState<boolean>(false);
    const [showSocialsModal, setShowSocialsModal] = useState(false);
    const [socialsForm] = Form.useForm();
    const [requestStatus, setRequestStatus] = useState<'none' | 'pending' | 'approved' | 'rejected'>('none');
    const [requestSubmitting, setRequestSubmitting] = useState(false);
    
    const currentUser: any = useSelector((state: RootState) => state.auth.user);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const socials = currentUser?.socials;
    const needsSocials =
        reviewNeeded &&
        ["promoter", "partner"].includes(role) &&
        (
            !socials ||
            !socials.bio ||
            !socials.links ||
            socials.links.length === 0
        );
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const transactions = (campaignData?.redeposits || [])
        .slice()
        .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map((r: any, idx: number) => ({
            key: r._id || idx,
            type: idx === 0 ? 'Initial budget' : 'Redeposit',
            amount: r.amount,
            timestamp: r.timestamp,
        }));

    const fetchWallet = async () => {
        try {
            const wallet = await walletService.getWallet();
            setWalletBalance(wallet.balance);
        } catch (e) {
        }
    };

    useEffect(() => {
        if (!showSocialsModal || !socials) return;

        socialsForm.setFieldsValue({
            bio: socials.bio || '',
            numberOfFollowers: socials.numberOfFollowers,
            links: socials.links?.length
                ? socials.links.map((link: any) => ({url: link.url}))
                : [{}]
        });
    }, [showSocialsModal, socials, socialsForm]);

    const fetchCampaignDetails = async () => {
        try {
            const data = await campaignService.getCampaignById(id as string);
            setCampaignData(data);
            setReviewNeeded(data.reviewNeeded || false);
            setRequestStatus(data.requestStatus || 'none');
            setIsBlocked(data.isBlocked || false);
        } catch (error) {
            console.error('Error fetching campaign details:', error);
        }
    };

    const handleCancelCampaign = async () => {
        try {
            const result = await campaignService.cancelCampaign(id as string);
            if (result?.status === 200) {
                message.success(result?.data.message);
                if (campaignData.status === 'draft') {
                    navigate('/campaigns/list');
                } else {
                    fetchCampaignDetails();
                }
            } else if (result?.data.code && result.data.code !== 'NO_ACTIVE_SUBSCRIPTION') {
                message.error('Something went wrong, campaign could not be cancelled.');
            }
        } catch (error: any) {
            message.error(error.message);
        }
    };

    const handlePromoteClick = () => {
        if (isBlocked)
            return;

        if (requestSubmitting)
            return;
        
        if (!reviewNeeded) {
            handlePromoteCampaign();
            return;
        }

        if (needsSocials) {
            setShowSocialsModal(true);
            return;
        }

        handleRequestPromotion();
    };
    
    const handlePromoteCampaign = async () => {
        try {
            const response = await campaignService.promoteCampaign(id as string);

            const campaignLinkId = response?.data.campaignLink?._id;
            if (campaignLinkId) {
                message.success('Campaign promoted successfully!');
                setPromotedCampaignLinkId(campaignLinkId);
            } else if (response?.data?.code !== 'NO_ACTIVE_SUBSCRIPTION') {
                message.error('Promotion failed: campaign link not returned.');
            } else if (response?.data?.code === 'NO_ACTIVE_SUBSCRIPTION') {
                message.error(`Promotion failed: ${response?.data?.error}`);
            }
        } catch (error: any) {
            message.error(error.message || 'Error promoting campaign.');
        }
    };

    const handlePublish = async () => {
        if (!campaignData || submitting) return;

        const geo = campaignData.geographicLocation;
        const hasGeoRadius = isRadiusLocation(geo);
        let geographicLocation = hasGeoRadius
            ? {
                country: null,
                state: null,
                city: null,
                lat: geo.lat,
                lng: geo.lng,
                radiusKm: geo.radiusKm || defaultRadiusKm,
            }
            : {
                country: geo?.country || null,
                state: geo?.state || null,
                city: geo?.city || null,
                lat: null,
                lng: null,
                radiusKm: null
            };

        if (geographicLocation.country) {
            const countryObj = (await countryService.getCountries())
                .find(c => c.name === geographicLocation.country || c.iso2 === geographicLocation.country);

            if (countryObj) {
                geographicLocation = {...geographicLocation, country: countryObj.iso2};
            }
        }

        const formData = new FormData();

        formData.append('title', campaignData.title);
        formData.append('description', campaignData.description || '');
        formData.append('targetURL', campaignData.targetURL);
        formData.append('clickRate', String(campaignData.clickRate));
        formData.append('initialBudget', String(campaignData.initialBudget));
        formData.append('geographicLocation', JSON.stringify(geographicLocation));
        formData.append('publish', 'true');
        formData.append('reviewNeeded', String(campaignData.reviewNeeded));

        // Append new files only if user selected them here
        if (logoFile) {
            formData.append('logo', logoFile);
        }
        if (thumbnailFile) {
            formData.append('thumbnail', thumbnailFile);
        }
        if (mediaFile) {
            formData.append('media', mediaFile);
        }

        setSubmitting(true);
        try {
            const result = await campaignService.updateCampaign(id as string, formData);
            if (result && result._id) {
                message.success('Campaign published!');
                // Reset local file overrides and refresh details
                setLogoFile(null);
                setThumbnailFile(null);
                setMediaFile(null);
                fetchCampaignDetails();
            } else {
                message.error('Failed to publish campaign. Please try again.');
            }
        } catch (error: any) {
            console.error('Error publishing campaign:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to publish campaign. Please try again.';
            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const onAmountSubmit = async () => {
        if (!depositAmount || depositAmount <= 0) return;

        try {
            if (depositMethod === 'wallet') {
                if (walletBalance < depositAmount) {
                    message.error('Not enough funds in wallet.');
                    return;
                }

                const response: any = await campaignService.redepositFromWallet(id as string, depositAmount);

                if (response?.status && response.status !== 200) {
                    message.error(response?.data?.message || 'Redeposit failed.');
                    return;
                }

                setPaymentOk(true);
                setDepositStep('result');
                await fetchCampaignDetails();
                await fetchWallet();
                return;
            }

            const response = await walletService.depositFunds({amount: depositAmount, campaignId: id});
            setClientSecret(response.clientSecret);
            setDepositStep('pay');
        } catch (e: any) {
            message.error(e.message || 'Error creating payment intent');
            setShowDepositModal(false);
        }
    };

    const handleRequestPromotion = async () => {
        if (!id) {
            message.error('Campaign ID is missing');
            return;
        }

        setRequestSubmitting(true);
        try {
            await campaignService.requestPromotion(id);
            setRequestStatus('pending');
            
            Modal.success({
                title: 'Request sent!',
                content: 'Your request has been sent successfully.'
            });

        } catch (e: any) {
            message.error(e.message || 'Failed to send request');
        } finally {
            setRequestSubmitting(false);
        }
    };

    const handleSubmitSocials = async (values: any) => {
        if (!id) {
            message.error('Campaign ID is missing');
            return;
        }

        setRequestSubmitting(true);
        try {
            // Detect platform
            const parsedLinks = values.links.map((l: any) => {
                const type = detectPlatform(l.url);

                if (!type) {
                    throw new Error(`Invalid or unsupported link: ${l.url}`);
                }

                return {
                    url: l.url,
                    type
                };
            });

            // Sanitize bio
            const safeBio = DOMPurify.sanitize(values.bio || '', {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li'],
                ALLOWED_ATTR: [],
            });

            if (!safeBio) {
                message.error('Please enter a short bio');
                return;
            }

            const payload = {
                bio: safeBio,
                numberOfFollowers: values.numberOfFollowers,
                links: parsedLinks
            };

            // 1. Save socials
            await authService.updateUserSocials(payload);

            // 2. Send request
            await campaignService.requestPromotion(id);
            setRequestStatus('pending');

            // 3. UX feedback
            Modal.success({
                title: 'Request sent!',
                content: (
                    <div>
                        <p>Your profile has been saved successfully.</p>
                        <p>A request has been sent to the campaign creator.</p>
                        <p>Please wait for approval.</p>
                    </div>
                )
            });

            setShowSocialsModal(false);

        } catch (e: any) {
            message.error(e.message || 'Failed to submit');
        } finally {
            setRequestSubmitting(false);
        }
    };

    useEffect(() => {
        const checkIfLinked = async () => {
            try {
                const links = await campaignLinkService.getCampaignLinks();
                const isLinked = links.some((link: any) => link.campaignId._id === id);
                setHasLinkedCampaign(isLinked);
            } catch (error) {
                console.error('Error checking linked campaigns:', error);
            }
        };

        fetchCampaignDetails();
        if (role === 'advertiser') fetchWallet();
        if (['promoter', 'partner'].includes(role)) checkIfLinked();
    }, []);

    useEffect(() => {
        if (walletBalance < 20 && depositMethod === 'wallet') {
            setDepositMethod('card');
        }
    }, [walletBalance, depositMethod]);

    if (isUpdating) navigate(`/campaigns/${id}/edit`);

    if (promotedCampaignLinkId) navigate(`/campaign-links/${promotedCampaignLinkId}`);

    if (!campaignData) return <p>Loading campaign details...</p>;

    const {
        title, geographicLocation, description, targetURL, clickCounter, clickRate,
        status, initialBudget, balance, expendedFunds, advertiserId
    } = campaignData;

    const assets = campaignData?.assets || {};

    const logoUrl = assets.logo?.signedUrl || null;
    const thumbnailUrl = assets.thumbnail?.signedUrl || null;
    const mediaUrl = assets.media?.signedUrl || null;
    const videoPreviewUrl = assets.video?.compressed?.signedUrl || null;
    const videoDownloadUrl = assets.video?.original?.signedUrl || null;

    const location = formatGeographicLocationLabel(geographicLocation);

    const isOwner = currentUser?.userId === advertiserId;

    const handleAssetDownload = (url: string | null, assetType: string) => {
        if (!url) return;

        downloadFile(url);

        if (['promoter', 'partner'].includes(role)) {
            trackEvent('campaign_assets_downloaded', {
                role,
                campaign_id: id,
                campaign_status: status,
                campaign_asset_type: assetType
            });
        }
    };

    return (
        <div style={{width: '100%', maxWidth: 1100, margin: '0 auto', padding: 16}}>
            {/* Title Section */}
            <label className={"title"}>Campaign details</label>

            {/* Info Section */}
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <div style={{marginBottom: 12}}><Text strong>Title:</Text> {title}</div>

                        <div style={{marginBottom: 12}}>
                            <Text strong>Description:</Text>
                            <div className="rtx-viewer" style={{marginTop: 6}}>
                                <RichTextViewer html={description}/>
                            </div>
                        </div>

                        <div style={{marginBottom: 12}}>
                            <Text strong>Target URL: </Text>
                            <Link href={targetURL} target="_blank" rel="noopener noreferrer"
                                  style={{wordBreak: 'break-all'}}>
                                {targetURL}
                            </Link>
                        </div>

                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tooltip title="The creator of this campaign needs to review promoters before they can promote this campaign.">
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <Text strong>Review needed:</Text>
                                    {reviewNeeded && ["promoter", "partner"].includes(role) && (
                                        <InfoCircleOutlined style={{ color: '#faad14' }} />
                                    )}
                                </span>
                            </Tooltip>

                            <Text>{reviewNeeded ? 'Yes' : 'No'}</Text>
                        </div>

                        <div style={{ marginTop: 12 }}>
                            <Text strong>Geographic location:</Text>

                            {isRadiusLocation(geographicLocation) ? (
                                <div style={{ marginTop: 10 }}>
                                    <GeoRadiusMap
                                        center={{lat: geographicLocation.lat, lng: geographicLocation.lng}}
                                        radiusKm={geographicLocation.radiusKm || defaultRadiusKm}
                                        height={260}
                                        readOnly
                                    />

                                    <div style={{ marginTop: 8 }}>
                                        <Text type="secondary">
                                            Radius: {geographicLocation.radiusKm || defaultRadiusKm} km
                                        </Text>
                                    </div>
                                </div>
                            ) : geographicLocation?.country || geographicLocation?.state || geographicLocation?.city ? (
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    {location}
                                </Text>
                            ) : (
                                <Text type="secondary" style={{ marginLeft: 8 }}>
                                    Global
                                </Text>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Metrics Section */}
            <Row gutter={[16, 16]} style={{marginTop: 20}}>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Click counter">{clickCounter}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Click rate">${formatValue(clickRate)}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Status">
                        <Badge
                            status={status === 'cancelled' ? 'error' : 'success'}
                            text={
                                <Text style={{color: status === 'cancelled' ? 'red' : 'green'}}>
                                    {formatString(status)}
                                </Text>
                            }
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{marginTop: 10}}>
                <Col xs={24} sm={12} md={8}><Card title="Initial budget">${formatValue(initialBudget)}</Card></Col>

                <Col xs={24} sm={12} md={8}>
                    <Card
                        title="Balance"
                        extra={
                            isOwner && role === 'advertiser' && status !== 'draft' && (
                                <a onClick={() => setShowTransactionModal(true)} style={{fontSize: 12}}>
                                    View transactions
                                </a>
                            )
                        }
                    >
                        ${formatValue(balance)}
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8}><Card title="Expended funds">${formatValue(expendedFunds)}</Card></Col>
            </Row>

            {isOwner && role === "advertiser" && status === "active" && (
                <Row gutter={[16, 16]} style={{marginTop: 10}}>
                    <Col xs={24} sm={12} md={8}>
                        <Card
                            title="Promoters"
                            extra={
                                <a onClick={() => navigate(`/campaigns/${id}/promoters`)} style={{fontSize: 12}}>
                                    View promoters
                                </a>
                            }
                        >
                            {campaignData.promotersCount || 0}
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Assets Section */}
            <Row gutter={[16, 16]} style={{marginTop: 10}}>
                <Col span={24}>
                    <Collapse
                        defaultActiveKey={[]} // collapsed by default
                        items={[
                            {
                                key: 'assets',
                                label: 'Campaign Assets',
                                children: (
                                    <Row gutter={[16, 16]}>
                                        {logoUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                            <Card
                                                size="small"
                                                title="Logo"
                                                actions={[
                                                    <Button key="download-logo" type="link"
                                                            onClick={() => handleAssetDownload(logoUrl, 'logo')}>
                                                        Download
                                                    </Button>
                                                ]}
                                                >
                                                    <Image
                                                        src={logoUrl}
                                                        preview={false}
                                                        style={{
                                                            width: '100%',
                                                            aspectRatio: '4 / 3',
                                                            objectFit: 'cover',
                                                            borderRadius: 6
                                                        }}
                                                        onClick={() => {
                                                            setPreviewImage(logoUrl);
                                                            setPreviewVisible(true);
                                                        }}
                                                    />
                                                </Card>
                                            </Col>
                                        )}

                                        {thumbnailUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                            <Card
                                                size="small"
                                                title="Thumbnail"
                                                actions={[
                                                    <Button key="download-thumbnail" type="link"
                                                            onClick={() => handleAssetDownload(thumbnailUrl, 'thumbnail')}>
                                                        Download
                                                    </Button>
                                                ]}
                                                >
                                                    <Image
                                                        src={thumbnailUrl}
                                                        preview={false}
                                                        style={{
                                                            width: '100%',
                                                            aspectRatio: '4 / 3',
                                                            objectFit: 'cover',
                                                            borderRadius: 6
                                                        }}
                                                        onClick={() => {
                                                            setPreviewImage(thumbnailUrl);
                                                            setPreviewVisible(true);
                                                        }}
                                                    />
                                                </Card>
                                            </Col>
                                        )}

                                        {mediaUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                            <Card
                                                size="small"
                                                title="Media"
                                                actions={[
                                                    <Button key="download-media" type="link"
                                                            onClick={() => handleAssetDownload(mediaUrl, 'media')}>
                                                        Download
                                                    </Button>
                                                ]}
                                                >
                                                    <Image
                                                        src={mediaUrl}
                                                        preview={false}
                                                        style={{
                                                            width: '100%',
                                                            aspectRatio: '4 / 3',
                                                            objectFit: 'cover',
                                                            borderRadius: 6
                                                        }}
                                                        onClick={() => {
                                                            setPreviewImage(mediaUrl);
                                                            setPreviewVisible(true);
                                                        }}
                                                    />
                                                </Card>
                                            </Col>
                                        )}

                                        {videoPreviewUrl && (
                                            <Col xs={24} sm={12} md={8}>
                                                <Card
                                                    size="small"
                                                    title="Video"
                                                    actions={[
                                                        <Button
                                                            key="download-video"
                                                            type="link"
                                                            onClick={() => handleAssetDownload(videoDownloadUrl, 'video')}
                                                        >
                                                            Download
                                                        </Button>
                                                    ]}
                                                >
                                                    <video
                                                        src={videoPreviewUrl}
                                                        controls
                                                        preload="metadata"
                                                        style={{
                                                            width: '100%',
                                                            borderRadius: 6,
                                                            maxHeight: 300,
                                                            objectFit: 'cover'
                                                        }}
                                                    >
                                                        Your browser does not support video.
                                                    </video>
                                                </Card>
                                            </Col>
                                        )}

                                        {!logoUrl && !thumbnailUrl && !mediaUrl && !videoPreviewUrl && (
                                            <Col span={24}>
                                                <Text type="secondary">No assets uploaded for this campaign.</Text>
                                            </Col>
                                        )}
                                    </Row>
                                )
                            }
                        ]}
                    />
                </Col>
            </Row>

            <Modal
                open={previewVisible}
                footer={null}
                onCancel={() => {
                    setPreviewVisible(false);
                    setPreviewImage(null);
                }}
                width={400}
                centered
            >
                {previewImage && (
                    <Image
                        src={previewImage}
                        alt="Preview"
                        style={{width: '100%'}}
                    />
                )}
            </Modal>

            <div style={{marginTop: 20}}>
                <div style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    justifyContent: isMobile ? 'stretch' : 'flex-end'
                }}>
                    {isOwner && role === "advertiser" && status === "draft" && (
                        <Button type="primary" block={isMobile} onClick={() => setIsUpdating(true)}>Update</Button>
                    )}
                    {isOwner && role === "advertiser" && status === "draft" && (
                        <Button block={isMobile}
                                style={{
                                    backgroundColor: "#52c41a",
                                    borderColor: "#52c41a",
                                    color: "#fff"
                                }}
                                onClick={handlePublish}
                                loading={submitting}
                                disabled={submitting}
                        >
                            Publish
                        </Button>
                    )}
                    {isOwner && role === "advertiser" && status === "active" && (
                        <Button type="primary" block={isMobile}
                                onClick={() => navigate(`/campaigns/${id}/edit-active`)}>
                            Update
                        </Button>
                    )}
                    {isOwner && role === "advertiser" && status === "active" && (
                        <Button type="primary" block={isMobile} onClick={() => {
                            setDepositAmount(null);
                            setDepositStep('input');
                            setDepositMethod('wallet')
                            setClientSecret(null);
                            setShowDepositModal(true);
                        }}
                        >
                            Redeposit
                        </Button>
                    )}
                    {isOwner && role === "advertiser" && ["draft", "active"].includes(status) && (
                        <Button type="default" block={isMobile} danger onClick={handleCancelCampaign}>
                            {status === 'active' ? 'Cancel campaign' : 'Delete draft'}
                        </Button>)}
                    {["promoter", "partner"].includes(role) && status === "active" && !hasLinkedCampaign && (
                        requestStatus === 'pending' ? (
                            <Button block={isMobile} disabled>
                                Request pending
                            </Button>
                        ) : requestStatus === 'approved' ? (
                            <Button block={isMobile} disabled>
                                Approved
                            </Button>
                        ) : requestStatus === 'rejected' ? (
                            <Button block={isMobile} onClick={handlePromoteClick} loading={requestSubmitting} disabled={requestSubmitting}>
                                Request again
                            </Button>
                        ) : (
                            <Button type="primary" block={isMobile} onClick={handlePromoteClick} loading={requestSubmitting} disabled={isBlocked || requestSubmitting}>
                                {isBlocked ? 'Blocked' : (reviewNeeded ? 'Request promotion' : 'Promote campaign')}
                            </Button>
                        )
                    )}
                    <Button block={isMobile} type="default" onClick={() => navigate(-1)}>Back</Button>
                </div>
            </div>

            <Modal visible={showDepositModal} title="Redeposit to Campaign" onCancel={() => setShowDepositModal(false)}
                   footer={null} destroyOnClose>
                {depositStep === 'input' && (
                    <Form layout="vertical" onFinish={onAmountSubmit}>
                        <Form.Item label="Payment method">
                            <Radio.Group value={depositMethod} onChange={(e) => setDepositMethod(e.target.value)}>
                                <Radio value="wallet" disabled={walletBalance < 20}>
                                    Wallet (available: ${formatValue(walletBalance)})</Radio>
                                <Radio value="card">Card</Radio>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item
                            label={`Amount (USD)`}
                            name="amount"
                            rules={[
                                {required: true, message: 'Please enter an amount'},
                                {
                                    validator: (_, value) => {
                                        if (!value || value < 20) {
                                            return Promise.reject(new Error('Minimum deposit is $20'));
                                        }

                                        if (depositMethod === 'wallet' && value > walletBalance) {
                                            return Promise.reject(
                                                new Error(`Amount cannot exceed wallet balance ($${formatValue(walletBalance)})`)
                                            );
                                        }

                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <InputNumber
                                style={{width: '100%'}}
                                min={0}
                                value={depositAmount || 0}
                                onChange={(v) => setDepositAmount(v!)}
                            />
                        </Form.Item>
                        <div style={{textAlign: 'right'}}>
                            <Button style={{marginRight: 8}} onClick={() => setShowDepositModal(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit">
                                {depositMethod === 'wallet' ? 'Redeposit from wallet' : 'Continue'}
                            </Button>
                        </div>
                    </Form>
                )}

                {depositStep === 'pay' && clientSecret && (
                    <Elements stripe={stripePromise} options={{clientSecret}}>
                        <DepositForm
                            onSuccess={() => {
                                setPaymentOk(true);
                                setDepositStep('result');
                                fetchCampaignDetails();
                            }}
                            onError={() => {
                                setPaymentOk(false);
                                setDepositStep('result');
                            }}
                        />
                    </Elements>
                )}

                {depositStep === 'result' &&
                    (paymentOk ? (
                        <Result
                            status="success"
                            title="Redeposit successful!"
                            extra={[
                                <Button
                                    key="back"
                                    type="primary"
                                    onClick={() => {
                                        setShowDepositModal(false);
                                        fetchCampaignDetails();
                                        message.success('Campaign balance updated');
                                    }}
                                >
                                    Back to Campaign
                                </Button>
                            ]}
                        />
                    ) : (
                        <Result
                            status="error"
                            title="Payment failed"
                            subTitle="Please try again."
                            extra={[
                                <Button key="retry" onClick={() => setDepositStep('input')}>Try Again</Button>
                            ]}
                        />
                    ))}
            </Modal>

            <Modal
                open={showTransactionModal}
                title={`Transactions — ${title}`}
                onCancel={() => setShowTransactionModal(false)}
                footer={null}
                width={500}
                destroyOnClose
            >
                <div style={{marginTop: 15}}>
                    <Table
                        size="small"
                        dataSource={transactions}
                        pagination={{pageSize: 10, size: 'small'}}
                        rowKey="key"
                        columns={[
                            {
                                title: 'Type',
                                dataIndex: 'type',
                                key: 'type'
                            },
                            {
                                title: 'Date',
                                dataIndex: 'timestamp',
                                key: 'timestamp',
                                render: (iso: string) =>
                                    new Date(iso).toLocaleString(undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    }),
                                sorter: (a: any, b: any) =>
                                    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
                                sortDirections: ['descend', 'ascend']
                            },
                            {
                                title: 'Amount (USD)',
                                dataIndex: 'amount',
                                key: 'amount',
                                align: 'right' as const,
                                render: (v: number) => `$${formatValue(v)}`,
                                sorter: (a: any, b: any) => a.amount - b.amount,
                            },
                        ]}
                        summary={(pageData) => {
                            const total = pageData.reduce((acc, r: any) => acc + (r.amount || 0), 0);
                            const grandTotal = (campaignData?.redeposits || []).reduce(
                                (acc: number, r: any) => acc + (r.amount || 0),
                                0
                            );
                            return (
                                <Table.Summary>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={1} colSpan={3} align="right">
                                            <b>Page total: ${formatValue(total)}</b>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={1} colSpan={3} align="right">
                                            <b>All redeposits: ${formatValue(grandTotal)}</b>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                </Table.Summary>
                            );
                        }}
                    />
                </div>
            </Modal>

            <Modal
                open={showSocialsModal}
                title="Complete your promoter profile"
                onCancel={() => setShowSocialsModal(false)}
                footer={null}
            >
                <Form form={socialsForm} layout="vertical" onFinish={handleSubmitSocials}>
                    
                    <Form.Item
                        label="Brief info about yourself"
                        name="bio"
                        rules={[
                            { required: true, message: 'Please enter a short bio' },
                            {
                                validator: (_, value) => (
                                    value
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('Please enter a short bio'))
                                )
                            }
                        ]}
                    >
                        <RichText placeholder="Tell us about yourself..." />
                    </Form.Item>
                    
                    <Form.Item
                        label="Total followers (optional)"
                        name="numberOfFollowers"
                    >
                        <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                    
                    <Form.List
                        name="links"
                        rules={[
                            {
                                validator: async (_, links) => {
                                    if (!links || links.length < 1) {
                                        return Promise.reject(new Error('At least one social link is required'));
                                    }
                                },
                            },
                        ]}
                    >
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name }) => (
                                    <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                        <Form.Item
                                            name={[name, 'url']}
                                            style={{ flex: 1 }}
                                            rules={[
                                                { required: true, message: 'Enter a link' },
                                                {
                                                    validator: (_, value) => {
                                                        if (!value) return Promise.resolve();

                                                        const type = detectPlatform(value);
                                                        if (!type) {
                                                            return Promise.reject(
                                                                new Error('Only Facebook, Instagram, TikTok, X, LinkedIn, YouTube, Twitch links are allowed')
                                                            );
                                                        }

                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            <Input placeholder="https://..." />
                                        </Form.Item>

                                        <Button danger onClick={() => remove(name)}>X</Button>
                                    </div>
                                ))}

                                <Button type="dashed" onClick={() => add()} block>
                                    Add social link
                                </Button>
                            </>
                        )}
                    </Form.List>

                    <div style={{ textAlign: 'right', marginTop: 12 }}>
                        <Button onClick={() => setShowSocialsModal(false)} style={{ marginRight: 8 }} disabled={requestSubmitting}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit" loading={requestSubmitting} disabled={requestSubmitting}>
                            Save & Continue
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default CampaignDetailPage;
